import { useCallback, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { TimeSignal, TimeSignalClassification } from 'graphql/types';
import { useNotesSelectionReset } from 'modules/notes/list/hooks';
import { FormTimesheetLabel } from '../../../timesheet-labels/labelSchema';
import { useFeatureFlag } from '../../../common/hooks';
import { useMultiEditTimeSignals } from './useMultiEditTimeSignals';
import { useMultiEditActions } from './useMultiEditActions';

export const useMultiEditBulkActionCallback = ({
  notes,
  getValues
}: {
  notes: TimeSignal[];
  getValues: UseFormReturn['getValues'];
}): {
  bulkActionCallback: (
    onNoteDiscard: () => void,
    classificationTarget: TimeSignalClassification
  ) => void;
  isActionInProgress: boolean;
} => {
  const [saving, setSaving] = useState(false);
  const { resetForm } = useNotesSelectionReset();
  const { handleMultiEditAction, isActionInProgress } = useMultiEditActions();
  const {
    onMultiEditTimeSignals,
    updateInProgress: multiEditInProgress
  } = useMultiEditTimeSignals();

  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const bulkActionCallback = useCallback(
    async (
      onNoteDiscard: () => void,
      classificationTarget: TimeSignalClassification
    ) => {
      const { timesheetLabels, customLabels } = getValues();
      const timesheetLabelValues = (
        (timesheetLabels as FormTimesheetLabel[]) || []
      ).map(({ label }: FormTimesheetLabel) => label);

      setSaving(true);

      if (isTimeSignalFederatedSuggestionsEnabled) {
        await onMultiEditTimeSignals({
          notes: notes,
          updates: {
            labelValues: timesheetLabelValues.concat(customLabels),
            classificationTarget: classificationTarget
          }
        });
      } else {
        await handleMultiEditAction({
          notes: notes,
          labelValues: timesheetLabelValues.concat(customLabels),
          classificationTarget
        });
      }
      onNoteDiscard();
      resetForm();
      setSaving(false);
    },
    [
      getValues,
      handleMultiEditAction,
      isTimeSignalFederatedSuggestionsEnabled,
      notes,
      onMultiEditTimeSignals,
      resetForm
    ]
  );

  return {
    bulkActionCallback,
    isActionInProgress:
      (isTimeSignalFederatedSuggestionsEnabled
        ? multiEditInProgress
        : isActionInProgress) || saving
  };
};
